/**
 * @file use-settings-form-state.ts
 *
 * Controlled-form state for the Settings form (no react-hook-form), mirroring
 * `use-category-form-state.ts`. Holds the editable fields, exposes a `patch`
 * updater, tracks dirtiness against the seed, and runs hand-rolled validation
 * matching WSB-v2's Shop settings (shop name, email + format, email language, and
 * currency are required; the rest are optional). Errors surface only after the
 * first `validate()` so the form doesn't shout at an untouched field. Re-seeds
 * when the seed reference changes (the screen passes a fresh seed once the data
 * loads).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { isValidEmail, shallowEqual } from './settings-form-utils';
import type { SettingsFormErrors, SettingsFormFields } from './settings-form-types';

/** What {@link useSettingsFormState} returns to the form. */
export interface UseSettingsFormStateResult {
  fields: SettingsFormFields;
  patch: (update: Partial<SettingsFormFields>) => void;
  isDirty: boolean;
  errors: SettingsFormErrors;
  validate: () => boolean;
  /** Attach to the shop-name field; focused/scrolled to when validation fails on it. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the Settings form's field state and validation.
 *
 * @param seed - The initial fields, derived from the loaded settings + shopfront
 *   locale; re-seeds the form when its reference changes.
 *
 * @returns The fields, updater, dirty flag, errors, and a `validate` function.
 */
export function useSettingsFormState(seed: SettingsFormFields): UseSettingsFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<SettingsFormFields>(seed);
  const initialRef = useRef<SettingsFormFields>(seed);
  const seededRef = useRef(seed);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Re-seed only when the source seed reference changes (e.g. it resolves after a
  // fetch); the initial value is already seeded by `useState`.
  useEffect(() => {
    if (seededRef.current === seed) {
      return;
    }
    seededRef.current = seed;
    setFields(seed);
    initialRef.current = seed;
    setSubmitted(false);
  }, [seed]);

  const patch = useCallback((update: Partial<SettingsFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: SettingsFormFields): SettingsFormErrors => {
      const errors: SettingsFormErrors = {};
      if (current.shopName.trim() === '') {
        errors.shopNameError = intl.formatMessage(adminMessages.settingsRequired);
      }
      if (current.email.trim() === '') {
        errors.emailError = intl.formatMessage(adminMessages.settingsRequired);
      } else if (!isValidEmail(current.email)) {
        errors.emailError = intl.formatMessage(adminMessages.settingsInvalidEmail);
      }
      if (current.emailLanguage === '') {
        errors.emailLanguageError = intl.formatMessage(adminMessages.settingsRequired);
      }
      if (current.currency === '') {
        errors.currencyError = intl.formatMessage(adminMessages.settingsRequired);
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.shopNameError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(() => !shallowEqual(fields, initialRef.current), [fields]);

  return { fields, patch, isDirty, errors, validate, firstErrorRef };
}
